import { error } from '@/lib/logger'
import pubsub from '@/lib/pubsub'
import isObject from 'lodash/isObject'
import type { EventProps } from '@/lib/marketing'

/**
 * This service is designed to explicitly communicate events to amplitude atop the `x-marketing-event` event
 * It's _only_ sending events to `x-marketing-event`. If you need other events, see `marketing.js`
 * There are two main ways to use this:
 * * * One is a direct call, basically a wrapper on `x-marketing-event`
 * * * The other allows you to build state within this service, for times when large complex events are required
 */

type PrimitiveEventValue = EventProps[keyof EventProps]
export type EventValue = PrimitiveEventValue | PrimitiveEventValue[]
export type EventData = Record<string, EventValue>
type EventStore = Record<string, EventData>

class MarketingEventService {
  private events: EventStore = {}

  constructor() {
    this.handleGlobalEvent = this.handleGlobalEvent.bind(this)
    window.addEventListener('global-marketing-event', this.handleGlobalEvent)
  }

  /**
   * Assign object data to the given eventName's object
   * @param eventName - Name of event
   * @param data - Object of key-value pairs to assign to event name
   */
  setEventValues(eventName: string, data: EventData): void {
    this.events[eventName] = { ...this.events[eventName], ...data }
  }

  /**
   * Get the current value of an event property. If no event or property exists, return false
   * @param eventName - Name of event
   * @param property - Name of event property
   * @returns False if doesn't exist, value if it does
   */
  getEventValue(eventName: string, property: string): EventValue {
    if (this.events?.[eventName]?.[property]) {
      return this.events[eventName][property]
    }
    return false
  }

  /**
   * Get all event values for an event. If no event or property exists, return false
   * @param eventName - Name of event
   * @returns False if doesn't exist, value if it does
   */
  getAllEventValues(eventName: string): EventData | false {
    if (this.events?.[eventName]) {
      return this.events[eventName]
    }
    return false
  }

  /**
   * Add item(s) to an array of unique items
   * This will strip any duplicate items, by design
   * @param eventName - Name of event
   * @param property - Name of event property
   * @param valuesArr - Array of values to add to property
   */
  appendToArray(
    eventName: string,
    property: string,
    valuesArr: PrimitiveEventValue[]
  ): void {
    // Add event name key if it doesn't exist
    if (!this.events[eventName]) {
      this.events[eventName] = {}
    }
    // Add event name property key if it doesn't exist
    if (!this.events[eventName][property]) {
      this.events[eventName][property] = []
    }
    // Ensure array of items is unique
    this.events[eventName][property] = [
      ...new Set([
        ...(this.events[eventName][property] as PrimitiveEventValue[]),
        ...valuesArr,
      ]),
    ]
  }

  /**
   * Add string to list of unique values eg "abc, xyz"
   * This will strip any duplicate items, by design
   * @param eventName - Name of event
   * @param property - Name of event property
   * @param value - String of value to add to property
   */
  appendToStringList(eventName: string, property: string, value: string): void {
    // Add event name key if it doesn't exist
    if (!this.events[eventName]) {
      this.events[eventName] = {}
    }
    // Add event name property key if it doesn't exist
    if (!this.events[eventName][property]) {
      this.events[eventName][property] = ''
    }
    // If string is empty, add value
    if (this.events[eventName][property] === '') {
      this.events[eventName][property] += value
    } else {
      // If there are string values, add value and convert it to a unique list
      this.events[eventName][property] += `, ${value}`
      this.events[eventName][property] = Array.from(
        new Set((this.events[eventName][property] as string).split(', '))
      ).join(', ')
    }
  }

  /**
   * Increase the count of a value by 1
   * @param eventName - Name of event
   * @param property - Name of event property
   */
  increaseCount(eventName: string, property: string): void {
    // Add event name key if it doesn't exist
    if (!this.events[eventName]) {
      this.events[eventName] = {}
    }
    // Add event name property key if it doesn't exist or reset if NaN
    if (
      !this.events[eventName][property] ||
      isNaN(this.events[eventName][property] as number)
    ) {
      this.events[eventName][property] = 0
    }
    // increment the value
    const current = this.events[eventName][property] as number
    this.events[eventName][property] = current + 1
  }

  /**
   * Delete an event from storage. Events are deleted when published
   * @param eventName - Name of event
   */
  clearEvent(eventName: string): void {
    if (this.events[eventName]) {
      const { [eventName]: _, ...remaining } = this.events
      this.events = remaining
    }
  }

  handleGlobalEvent(event: Event): void {
    const customEvent = event as CustomEvent<{ name?: string }>
    if (customEvent.detail && customEvent.detail.name) {
      const { name, ...rest } = customEvent.detail
      this.publish(name, rest as EventData)
    } else {
      throw new Error('Missing required event properties')
    }
  }

  /**
   * Publish event
   * @param eventName - Name of event
   * @param data - Optional object of key-value pairs to assign to event name
   */
  publish(eventName = '', data: EventData = {}): void {
    if (this.isFlatObject(data)) {
      this.setEventValues(eventName, data)
    } else {
      error(
        'Marketing',
        'unpublishable event:',
        eventName,
        'Nested event properties are not supported',
        data
      )

      return
    }

    // Ensure every event has a `name` property before sending
    if (!this.events[eventName].name) {
      this.setEventValues(eventName, { name: eventName })
    }
    pubsub.publish('x-marketing-event', this.events[eventName])
    this.clearEvent(eventName)
  }

  /**
   * Check if input object has nested properties.
   * @param obj - The input Object
   * @returns False if obj contains a non-array object, true otherwise
   */
  isFlatObject(obj: object): boolean {
    for (const key in obj) {
      const prop = (obj as Record<string, unknown>)[key]
      if (isObject(prop) && !Array.isArray(prop)) {
        return false
      }
      if (Array.isArray(prop) && !this.isFlatObject(prop)) {
        return false
      }
    }
    return true
  }
}

export default new MarketingEventService()
